import {
  Matrix3,
  Object3D,
  Quaternion,
  Vector3,
} from "three";
import { OBB } from "three/examples/jsm/math/OBB.js";
import { createVolumeDebugWire } from "../../../debug/volumeDebugWire";
import {
  isObbDebugDrawEnabled,
  volumeDebugColor,
} from "../../../util/DebugFlags";
import { SugarModel } from "../../../loader/SugarModel";
import { Room } from "../../../shape/Scene";
import type { FaceLatchMap } from "../collision/ModelAligner";
import {
  type CollisionWorkingSet,
  type NarrowCollisionResolveResult,
  type NarrowHit,
  buildLineWallObstacles,
  buildObstacles,
  computeNarrowCollisionResolution,
  createCollisionWorkingSet,
  narrowPhaseIntersections,
  resolveContactLayer,
  worldSpaceBroadOBB,
  worldSpaceCompoundOBBs,
} from "../collision/ObstacleCollision";
import type {
  AppliedPushDebug,
  CornerLineSnapDebug,
  Obstacle,
  SeparationDebug,
} from "../collision/ObjectCollisionHits";
import type { DragSessionConstraint } from "../DragSessionPort";
import type {
  PlanarGraphWallCollisionProvider,
  SegmentWallCollisionShape,
} from "../collision/PlanarGraphWallCollisionProvider";
import {
  flattenPathAdaptive,
  pathNormalAt,
  pathPointAt,
  projectPointOnPath,
} from "../../../shape/segment/axis/pathHelpers";
import { thicknessSidesOf } from "../../../shape/segment/thicknessSides";
import type { XzPoint } from "../../../shape/segment/types";
import { isStructuralFloorFreeKind } from "../../../shape/structural/StructuralCatalog";

function structuralKindOf(
  model: SugarModel,
): string | undefined {
  return (model.data as { structuralKind?: string } | undefined)
    ?.structuralKind;
}

/** Columns / beams / outdoor props: FLOOR push-out only (never wall snap). */
function isStructuralFloorKind(model: SugarModel): boolean {
  return isStructuralFloorFreeKind(structuralKindOf(model));
}

/**
 * Columns/beams: push-out only (no wall snap / yaw lock) — structural catalog.
 * All other products keep segment wall snap + rotate-align (pre-structural behavior).
 */
function allowsWallSnap(model: SugarModel): boolean {
  return !isStructuralFloorKind(model);
}

export class WallCollisionConstraint implements DragSessionConstraint {
  private _working?: CollisionWorkingSet;
  private _grabQuaternion = new Quaternion();
  private _grabPosePosition = new Vector3();
  private _lastCandidatePosition = new Vector3();
  private _lastHits: NarrowHit[] = [];
  private _lastSeparations: SeparationDebug[] = [];
  private _lastAppliedPush: AppliedPushDebug | undefined;
  private _lastAppliedPushes: AppliedPushDebug[] = [];
  private _lastCornerSnaps: CornerLineSnapDebug[] = [];
  private _faceLatches: FaceLatchMap = new Map();
  private _ignoredSupport?: SugarModel;
  private _lastSegmentWallContact?: {
    segmentId: string;
    normal: Vector3;
  };
  private _lastSegmentWallContacts: {
    segmentId: string;
    normal: Vector3;
  }[] = [];
  private _alignmentLock?: {
    segmentId: string;
    normal: Vector3;
  };
  private _stableAlignment?: {
    segmentId: string;
    normal: Vector3;
  };
  private _wallFaces = new Map<string, { segmentId: string; normal: Vector3 }>();
  private _selfDebug: Object3D[] = [];
  private _selfDebugRoot?: Object3D;

  constructor(
    private _scene: Room,
    private readonly _segmentWalls?: PlanarGraphWallCollisionProvider,
  ) {}

  private _ensureDebugRoot(parent: Object3D | null | undefined): Object3D {
    if (this._selfDebugRoot && this._selfDebugRoot.parent)
      return this._selfDebugRoot;
    const root = new Object3D();
    root.name = "self-obb-debug-root";
    (parent ?? (this._scene as any)).add?.(root);
    this._selfDebugRoot = root;
    return root;
  }

  private _clearSelfDebug() {
    this._selfDebug.forEach((m) => {
      m.removeFromParent();
      const mesh = m as Object3D & {
        geometry?: { dispose?: () => void };
        material?: { dispose?: () => void };
      };
      mesh.geometry?.dispose?.();
      mesh.material?.dispose?.();
    });
    this._selfDebug = [];
  }

  private _drawObbWire(
    obb: OBB,
    color: number,
    renderOrder: number,
    parent: Object3D | null | undefined,
  ) {
    const wire = createVolumeDebugWire(obb, color);
    wire.renderOrder = renderOrder;
    this._ensureDebugRoot(parent).add(wire);
    this._selfDebug.push(wire);
  }

  private _syncSelfDebug(model: SugarModel, position: Vector3) {
    if (!this._working) return;
    this._clearSelfDebug();

    //debug
    if (isObbDebugDrawEnabled()) {
      let p: Object3D | null = model.parent;
      while (p && p.parent) p = p.parent;
      const root = p ?? model;
      const broad = worldSpaceBroadOBB(this._working, position);
      this._drawObbWire(broad, volumeDebugColor("hull"), 9999, root);
      for (const obb of worldSpaceCompoundOBBs(this._working, position)) {
        this._drawObbWire(obb, volumeDebugColor("parts"), 9998, root);
      }
    }
  }

  onDragStart(model: SugarModel): void {
    this._stableAlignment = undefined;
    this._faceLatches.clear();
    this._captureWorkingSet(model);
  }

  onLateDragStart(model: SugarModel): void {
    if (!this._working || this._working.excludeModel !== model) return;
    model.updateMatrixWorld(true);
    if (this._grabWorldMatrixMatches(model)) return;
    this._captureWorkingSet(model);
  }

  private _grabWorldMatrixMatches(model: SugarModel): boolean {
    if (!this._working) return true;
    const a = this._working.grabWorldMatrix.elements;
    const b = model.matrixWorld.elements;
    for (let i = 0; i < 16; i++) {
      if (Math.abs(a[i] - b[i]) > 1e-5) return false;
    }
    return true;
  }

  private _captureWorkingSet(model: SugarModel): void {
    model.updateMatrixWorld(true);
    this._working = createCollisionWorkingSet(model, model.matrixWorld.clone());
    this._grabPosePosition.copy(model.position);
    this._grabQuaternion.copy(model.quaternion);
    this._lastCandidatePosition.copy(model.position);
    this._lastHits = [];
    this._lastSeparations = [];
    this._lastAppliedPush = undefined;
    this._lastAppliedPushes = [];
    this._lastCornerSnaps = [];
  }

  apply(model: SugarModel, position: Vector3): Vector3 {
    this._lastSegmentWallContact = undefined;
    if (!this._alignmentLock) this._lastSegmentWallContacts = [];
    this._lastCandidatePosition.copy(position);

    if (!this._working || this._working.excludeModel !== model) {
      this._lastHits = [];
      this._lastSeparations = [];
      this._lastAppliedPush = undefined;
      this._lastAppliedPushes = [];
      this._lastCornerSnaps = [];
      return position;
    }

    const layers = this._resolveLayers(position);
    this._rememberLayers(layers);
    this._syncSelfDebug(model, layers.wall.position);

    //debug
    if (isObbDebugDrawEnabled()) {
      let p: Object3D | null = model.parent;
      while (p && p.parent) p = p.parent;
      const debugRoot = p ?? model;
      for (const h of this._lastHits) {
        this._drawObbWire(h.obstacle.obb, volumeDebugColor("contact"), 9997, debugRoot);
      }
    }

    const wallSnap = allowsWallSnap(model);
    const wallPlacement = wallSnap
      ? wallCollisionPlacement(
          this._working,
          layers.wall.hitsAtCandidate,
          layers.wall.position,
        )
      : undefined;
    if (wallPlacement) {
      model.quaternion.copy(wallPlacement.rotation);
      this._alignmentLock = undefined;
      return layers.wall.position;
    } else if (layers.model.rotation && !isStructuralFloorKind(model)) {
      model.quaternion.copy(layers.model.rotation);
    } else if (isStructuralFloorKind(model)) {
      model.quaternion.copy(this._grabQuaternion);
    } else if (!this._segmentWalls) {
      model.quaternion.copy(this._grabQuaternion);
    }

    const segmentResolved = this._resolveSegmentWalls(
      model,
      position,
      layers.wall.position,
      wallSnap ? this._alignmentLock : undefined,
      wallSnap,
      layers.wall.separations,
    );
    this._alignmentLock = undefined;
    return segmentResolved;
  }

  private _resolveLayers(position: Vector3): {
    model: NarrowCollisionResolveResult;
    wall: NarrowCollisionResolveResult;
  } {
    const model = computeNarrowCollisionResolution(
      this._scene,
      this._working!,
      position,
      this._ignoredSupport,
      this._faceLatches,
    );
    const wall = resolveContactLayer(
      this._working!,
      model.position,
      (broad) => this._wallLayer(broad),
      { cornerSnap: false },
    );
    return { model, wall };
  }

  private _rememberLayers(layers: {
    model: NarrowCollisionResolveResult;
    wall: NarrowCollisionResolveResult;
  }): void {
    this._lastHits = [
      ...layers.model.hitsAtCandidate,
      ...layers.wall.hitsAtCandidate,
    ];
    this._lastSeparations = [
      ...layers.model.separations,
      ...layers.wall.separations,
    ];
    this._lastAppliedPushes = [layers.model.appliedPush, layers.wall.appliedPush].filter(
      (push): push is AppliedPushDebug => push != null,
    );
    this._lastAppliedPush = this._lastAppliedPushes.at(-1);
    this._lastCornerSnaps = layers.model.cornerSnaps;
  }

  /** Duvar katmanının OBB’leri. ContactApi tür bakmaz; liste burada kurulur. */
  private _wallLayer(broad: OBB): Obstacle[] {
    const out = buildLineWallObstacles(this._scene, broad);
    for (const wall of this._segmentObstacles()) {
      if (broad.intersectsOBB(wall.obb)) out.push(wall);
    }
    return out;
  }

  private _segmentObstacles(): Obstacle[] {
    this._wallFaces.clear();
    if (!this._segmentWalls) return [];
    const out: Obstacle[] = [];
    for (const wall of this._segmentWalls.shapes()) {
      const spans = wallSpans(wall);
      for (let index = 0; index < spans.length; index++) {
        const slab = slabObb(spans[index], wall.minY, wall.maxY, wall.spec);
        if (!slab) continue;
        const latchKey = `segment:${wall.segmentId}:${index}`;
        const faceNormal = new Vector3(slab.faceX, 0, slab.faceZ).multiplyScalar(
          wall.interiorSide,
        );
        this._wallFaces.set(latchKey, {
          segmentId: wall.segmentId,
          normal: faceNormal,
        });
        out.push({
          kind: "wall",
          obb: slab.obb,
          latchKey,
          faceNormal,
        });
      }
    }
    return out;
  }

  private _resolveSegmentWalls(
    model: SugarModel,
    measureAt: Vector3,
    resolved: Vector3,
    alignmentLock?: { segmentId: string; normal: Vector3 },
    alignToWall = true,
    separations: readonly SeparationDebug[] = [],
  ): Vector3 {
    if (!this._working || !this._segmentWalls) return resolved;
    if (!alignToWall) {
      this._stableAlignment = undefined;
      return resolved;
    }
    const stableAlignment =
      alignmentLock ??
      (this._stableAlignment &&
      this._alignmentStillNear(measureAt, this._stableAlignment)
        ? this._stableAlignment
        : undefined);
    if (!stableAlignment && !alignmentLock) {
      this._stableAlignment = undefined;
    }
    let alignmentNormal = stableAlignment?.normal.clone();
    let strongestPenetration = stableAlignment
      ? Number.POSITIVE_INFINITY
      : 0;
    if (stableAlignment) {
      this._lastSegmentWallContact = {
        segmentId: stableAlignment.segmentId,
        normal: stableAlignment.normal.clone(),
      };
      if (
        !this._lastSegmentWallContacts.some(
          (contact) => contact.segmentId === stableAlignment.segmentId,
        )
      ) {
        this._lastSegmentWallContacts.push({
          segmentId: stableAlignment.segmentId,
          normal: stableAlignment.normal.clone(),
        });
      }
    }
    for (const separation of separations) {
      const face = this._wallFaces.get(separation.obstacleKey);
      if (!face) continue;
      if (
        !this._lastSegmentWallContacts.some(
          (contact) => contact.segmentId === face.segmentId,
        )
      ) {
        this._lastSegmentWallContacts.push({
          segmentId: face.segmentId,
          normal: face.normal.clone(),
        });
      }
      if (!stableAlignment && separation.depth > strongestPenetration) {
        strongestPenetration = separation.depth;
        alignmentNormal = face.normal.clone();
        this._lastSegmentWallContact = {
          segmentId: face.segmentId,
          normal: alignmentNormal.clone(),
        };
      }
    }
    if (alignmentNormal) {
      model.quaternion.setFromUnitVectors(WORLD_FORWARD, alignmentNormal);
      if (this._lastSegmentWallContact) {
        this._stableAlignment = {
          segmentId: this._lastSegmentWallContact.segmentId,
          normal: this._lastSegmentWallContact.normal.clone(),
        };
      }
    }
    return resolved;
  }

  private _alignmentStillNear(
    position: Vector3,
    alignment: { segmentId: string; normal: Vector3 },
  ): boolean {
    if (!this._working || !this._segmentWalls) return false;
    const wall = this._segmentWalls
      .shapes()
      .find((candidate) => candidate.segmentId === alignment.segmentId);
    if (!wall) return false;
    for (const obb of dragSelfObbs(this._working, position)) {
      const projection = projectPointOnPath(wall.spec.path, {
        x: obb.center.x,
        z: obb.center.z,
      });
      const pathNormal = new Vector3(
        projection.normal.x,
        0,
        projection.normal.z,
      ).normalize();
      const signed = new Vector3(
        obb.center.x - projection.point.x,
        0,
        obb.center.z - projection.point.z,
      ).dot(pathNormal);
      const direction = signed >= 0 ? pathNormal : pathNormal.negate();
      const wallHalf =
        signed >= 0 ? wall.spec.upThickness : wall.spec.downThickness;
      const releaseDistance = wallHalf + obbSupport(obb, direction) + 0.12;
      if (Math.abs(signed) <= releaseDistance) return true;
    }
    return false;
  }

  onDragEnd(model: SugarModel): void {
    if (this._working && this._working.excludeModel === model) {
      const wallSnap = allowsWallSnap(model);
      const layers = this._resolveLayers(this._lastCandidatePosition);
      const wallPlacement = wallSnap
        ? wallCollisionPlacement(
            this._working,
            layers.wall.hitsAtCandidate,
            layers.wall.position,
          )
        : undefined;
      model.position.copy(
        wallPlacement
          ? layers.wall.position
          : this._resolveSegmentWalls(
              model,
              this._lastCandidatePosition,
              layers.wall.position,
              undefined,
              wallSnap,
              layers.wall.separations,
            ),
      );

      if (wallPlacement) {
        model.quaternion.copy(wallPlacement.rotation);
      } else if (layers.model.rotation && !isStructuralFloorKind(model)) {
        model.quaternion.copy(layers.model.rotation);
      } else if (isStructuralFloorKind(model)) {
        model.quaternion.copy(this._grabQuaternion);
      } else if (!this._segmentWalls) {
        model.quaternion.copy(this._grabQuaternion);
      }
      model.updateWorldMatrix(true, true);
    }
    this._working = undefined;
    this._ignoredSupport = undefined;
    this._stableAlignment = undefined;
    this._faceLatches.clear();
    this._lastHits = [];
    this._lastSeparations = [];
    this._lastAppliedPush = undefined;
    this._lastAppliedPushes = [];
    this._lastCornerSnaps = [];
    this._clearSelfDebug();
  }

  /** Working set'in var olup olmadığı (fallback collision hit emit için). */
  get working(): CollisionWorkingSet | undefined {
    return this._working;
  }

  /** Son apply() çağrısının collision isabetleri; collision hit event için. */
  get lastHits(): NarrowHit[] {
    return this._lastHits;
  }

  get lastSeparations(): SeparationDebug[] {
    return this._lastSeparations;
  }

  get lastAppliedPush(): AppliedPushDebug | undefined {
    return this._lastAppliedPush;
  }

  get lastAppliedPushes(): readonly AppliedPushDebug[] {
    return this._lastAppliedPushes;
  }

  get lastCornerSnaps(): readonly CornerLineSnapDebug[] {
    return this._lastCornerSnaps;
  }

  get lastSegmentWallContact():
    | { segmentId: string; normal: Vector3 }
    | undefined {
    return this._lastSegmentWallContact;
  }

  get lastSegmentWallContacts(): readonly {
    segmentId: string;
    normal: Vector3;
  }[] {
    return this._lastSegmentWallContacts;
  }

  lockAlignmentForNextApply(contact: {
    segmentId: string;
    normal: Vector3;
  }): void {
    this._alignmentLock = {
      segmentId: contact.segmentId,
      normal: contact.normal.clone(),
    };
  }

  get grabPosePosition(): Vector3 {
    return this._grabPosePosition;
  }

  get grabPoseQuaternion(): Quaternion {
    return this._grabQuaternion;
  }

  setIgnoredSupport(model: SugarModel | undefined): void {
    this._ignoredSupport = model;
  }

  /** Working set'i yeniden kurar (model mesh'i güncellendikten sonra). */
  refresh(model: SugarModel): void {
    this._captureWorkingSet(model);
  }

  /** Broad + compound OBB'leri dünya uzayında hesaplar (collision hit event için). */
  computeHitsAtPosition(model: SugarModel, position: Vector3): NarrowHit[] {
    if (!this._working) return [];
    const broad = worldSpaceBroadOBB(this._working, position);
    const compound = worldSpaceCompoundOBBs(this._working, position);
    const obstacles = [
      ...buildObstacles(this._scene, broad, model, this._ignoredSupport),
      ...this._wallLayer(broad),
    ];
    return narrowPhaseIntersections(compound, obstacles);
  }
}

type WallPlacement = {
  position: Vector3;
  rotation: Quaternion;
};

const WORLD_FORWARD = new Vector3(0, 0, 1);
const _supportLocal = new Vector3();
const _rotationMatrix = new Matrix3();

function dragSelfObbs(working: CollisionWorkingSet, position: Vector3): OBB[] {
  const compound = worldSpaceCompoundOBBs(working, position);
  return compound.length > 0
    ? compound
    : [worldSpaceBroadOBB(working, position)];
}

function obbSupport(
  obb: { rotation: Matrix3; halfSize: Vector3 },
  direction: Vector3,
): number {
  _supportLocal
    .copy(direction)
    .applyMatrix3(_rotationMatrix.copy(obb.rotation).transpose());
  _supportLocal.set(
    Math.abs(_supportLocal.x),
    Math.abs(_supportLocal.y),
    Math.abs(_supportLocal.z),
  );
  return _supportLocal.dot(obb.halfSize);
}

function wallCollisionPlacement(
  _working: CollisionWorkingSet,
  hits: NarrowHit[],
  modelPosition: Vector3,
): WallPlacement | undefined {
  const wallHit = hits.find(
    (hit) => hit.obstacle.kind === "wall" && hit.obstacle.line,
  );
  const wallRotation = wallHit?.obstacle.line?.rotation?.clone();
  if (!wallRotation) return undefined;

  const forward = WORLD_FORWARD.clone().applyQuaternion(wallRotation);
  forward.y = 0;
  if (forward.lengthSq() < 1e-8) return undefined;

  return { position: modelPosition.clone(), rotation: wallRotation };
}

function wallSpans(
  wall: SegmentWallCollisionShape,
): Array<{ a: XzPoint; b: XzPoint; normal: XzPoint }> {
  const path = wall.spec.path;
  if (!path.control) {
    return [
      {
        a: pathPointAt(path, 0),
        b: pathPointAt(path, 1),
        normal: pathNormalAt(path, 0),
      },
    ];
  }
  const samples = flattenPathAdaptive(path);
  const spans: Array<{ a: XzPoint; b: XzPoint; normal: XzPoint }> = [];
  for (let i = 0; i < samples.length - 1; i++) {
    const t = (samples[i].t + samples[i + 1].t) * 0.5;
    spans.push({
      a: samples[i].point,
      b: samples[i + 1].point,
      normal: pathNormalAt(path, t),
    });
  }
  return spans;
}

function slabObb(
  span: { a: XzPoint; b: XzPoint; normal: XzPoint },
  minY: number,
  maxY: number,
  spec: SegmentWallCollisionShape["spec"],
): { obb: OBB; faceX: number; faceZ: number } | undefined {
  const dx = span.b.x - span.a.x;
  const dz = span.b.z - span.a.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6 || maxY - minY < 1e-6) return undefined;
  const lx = dx / len;
  const lz = dz / len;
  // Kalınlık yönü, yol normali. OBB Z ise sağ-el: quaternion teli aynı eksene oturur.
  const faceX = lz;
  const faceZ = -lx;
  const { upThickness, downThickness } = thicknessSidesOf(spec);
  const shift = (upThickness - downThickness) * 0.5;
  const obb = new OBB();
  obb.center.set(
    (span.a.x + span.b.x) * 0.5 + faceX * shift,
    (minY + maxY) * 0.5,
    (span.a.z + span.b.z) * 0.5 + faceZ * shift,
  );
  obb.halfSize.set(
    len * 0.5,
    (maxY - minY) * 0.5,
    (upThickness + downThickness) * 0.5,
  );
  obb.rotation.set(
    lx,
    0,
    -faceX,
    0,
    1,
    0,
    lz,
    0,
    -faceZ,
  );
  return { obb, faceX, faceZ };
}
